'use client';

import { useId, useState } from 'react';
import { Globe, ChevronDown, ChevronUp, Search, XCircle } from 'lucide-react';
import { Streamdown } from 'streamdown';
import { code } from '@streamdown/code';
import { math } from '@streamdown/math';

interface WebSearchOutput {
  success: boolean;
  query: string;
  results?: string;
  error?: string;
}

/**
 * Custom UI for the webSearch tool result.
 * Shows the search query and results in a collapsible card.
 */
export function WebSearchToolOutput({ output }: { output: WebSearchOutput }) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  if (!output.success) {
    return (
      <div
        className="flex items-center gap-2.5 py-2.5 px-3.5 rounded-lg bg-destructive/10 border border-destructive/30"
        role="alert"
        aria-live="polite"
      >
        <XCircle className="w-4 h-4 text-destructive flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium text-destructive">
          Web search failed
        </span>
        {output.error && (
          <span className="text-xs text-destructive/70 truncate ml-1">
            — {output.error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 overflow-hidden">
      {/* Header — always visible, clickable to toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="w-full flex items-center gap-2.5 py-2.5 px-3.5 hover:bg-muted/50 transition-colors"
      >
        <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" aria-hidden="true" />
        <Search className="w-3 h-3 text-muted-foreground flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-medium text-foreground truncate">
          {output.query}
        </span>
        <span className="ml-auto flex-shrink-0 text-muted-foreground" aria-hidden="true">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
        <span className="sr-only">
          {expanded ? 'Collapse results' : 'Expand results'}
        </span>
      </button>

      {/* Results — collapsible */}
      {expanded && output.results && (
        <div id={panelId} className="px-3.5 pb-3 border-t border-border/40">
          <Streamdown
            className="mt-2.5 text-sm text-foreground/85 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
            plugins={{ code, math }}
          >
            {output.results}
          </Streamdown>
        </div>
      )}
    </div>
  );
}
